import React, { useRef, useState } from 'react';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import { Background, Main } from 'styles/themeColors';
import Tabs from 'components/UI/Tabs';
import { GENERAL_CONSTANTS } from 'constants/general';
import Page from 'components/UI/Page';
import WarningBanner from 'components/UI/WarningBanner';
import { BusinessUnit } from 'data/BusinessUnit';
import FeatureFlag from 'data/FeatureFlag';
import Vendor from 'data/Vendor';
import { AFFILIATE_SETTINGS_TABS } from 'constants/affiliate';
import styled from 'styled-components';

const EmailNotificationsTab = React.lazy(() => import('./EmailNotificationsTab'));

interface Props {
  selectedVendor: Vendor;
  selectedBusinessUnit?: BusinessUnit;
  featureFlags?: FeatureFlag;
}

const EmptyMessage = styled.span`
  font-size: 24px;
  padding: 48px 24px;
`;

const AffiliateSettings: React.FC<Props> = ({ selectedVendor, selectedBusinessUnit, featureFlags }: Props) => {
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const emailNotificationsRef = useRef<{ handleSave: () => void } | null>(null);

  const tabRefs = [emailNotificationsRef];

  const onSave = () => {
    tabRefs[activeTab]?.current?.handleSave?.();
  };

  const handleTabChange = (tabIndex: number | undefined) => {
    if (tabIndex !== undefined) {
      setActiveTab(tabIndex);
      setIsSaveDisabled(false);
      setHasUnsavedChanges(false);
    }
  };

  return (
    <Page bgColor={Background.white} padding='24px 12px 12px 12px'>
      {
        !selectedBusinessUnit ? <EmptyMessage>No business unit selected</EmptyMessage> : 
        <Tabs
          theme="brandDark"
          height="36px !important"
          borderRadius="20px"
          tabPanelPadding="0 !important"
          tabs={AFFILIATE_SETTINGS_TABS}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          padding="0px 24px 24px 24px"
          className='brands-tabs'
          actionButtons={
          <FlexboxContainerComponent width="100%" border="none" alignItems="center">
            <div style={{ border: 'none', display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              {hasUnsavedChanges && !isSaveDisabled && (
                <WarningBanner
                  message={GENERAL_CONSTANTS.UNSAVED_CHANGES_DETAILED}
                  fontSize="14px"
                  margin="0px 10px 0px 0px"
                />
              )}
              <Button
                borderColor={Main.brandDark}
                background={Main.brandDark}
                key="save"
                onClick={onSave}
                disabled={isSaveDisabled}
              >
                {GENERAL_CONSTANTS.SAVE}
              </Button>
            </div>
          </FlexboxContainerComponent>
        }
      >
        <Page key="email-notifications" padding="0 24px 0 24px">
          <React.Suspense fallback={null}>
            <EmailNotificationsTab
              ref={emailNotificationsRef}
              setIsSaveDisabled={setIsSaveDisabled}
              setHasUnsavedChanges={setHasUnsavedChanges}
              brandId={selectedBusinessUnit?.brandId}
            />
          </React.Suspense>
        </Page>
      </Tabs>
      }
    </Page>
  );
};

export default AffiliateSettings;
